import React, { useState, Component } from 'react';
import { CheckIcon, XIcon, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
export const HeroSection = () => {
  const [address, setAddress] = useState('');
  const [checkResult, setCheckResult] = useState<null | {
    inRange: boolean;
    driveTime: number | null;
    shippingCost: number;
  }>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const BASE_ADDRESS = '3186 Garden Hill Lane, Douglasville, GA 30135';
  const MAPQUEST_API_KEY = 'ZUdH7N3IhErM5jO0rtgyCtZ4tWTth0v5';
  const calculateShippingCost = (driveTimeMinutes: number): number => {
    if (driveTimeMinutes <= 30) return 0;
    if (driveTimeMinutes <= 45) return 25;
    if (driveTimeMinutes <= 60) return 50;
    return -1;
  };
  const checkDeliveryDistance = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedAddress = address.trim();
    if (!trimmedAddress || trimmedAddress.length < 5) {
      setErrorMessage('Please enter a valid ZIP code');
      return;
    }
    // Check if it's just a ZIP code (5 digits)
    const zipOnlyMatch = trimmedAddress.match(/^\d{5}$/);
    if (zipOnlyMatch) {
      // User entered just a ZIP code, append to base city/state for routing
      const fullAddress = `${trimmedAddress}, Georgia`;
      await calculateRoute(fullAddress);
      return;
    }
    // Check if user entered the same address as base
    const normalizedInput = trimmedAddress.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedBase = BASE_ADDRESS.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedInput === normalizedBase) {
      setCheckResult({
        inRange: true,
        driveTime: 0,
        shippingCost: 0
      });
      setErrorMessage('');
      return;
    }
    await calculateRoute(trimmedAddress);
  };
  const calculateRoute = async (address: string) => {
    setIsChecking(true);
    setErrorMessage('');
    setCheckResult(null);
    try {
      const url = `https://www.mapquestapi.com/directions/v2/route?key=${MAPQUEST_API_KEY}&from=${encodeURIComponent(BASE_ADDRESS)}&to=${encodeURIComponent(address)}`;
      const response = await fetch(url);
      const data = await response.json();
      console.log('MapQuest Response:', data);
      if (data.route && data.route.time) {
        const driveTimeMinutes = Math.ceil(data.route.time / 60);
        const shippingCost = calculateShippingCost(driveTimeMinutes);
        setCheckResult({
          inRange: shippingCost !== -1,
          driveTime: driveTimeMinutes,
          shippingCost: shippingCost
        });
      } else if (data.info && data.info.statuscode !== 0) {
        const statusCode = data.info.statuscode;
        if (statusCode === 402 || statusCode === 611) {
          setErrorMessage('Unable to find a route to that location. Please verify the ZIP code is correct.');
        } else {
          setErrorMessage('Unable to calculate distance. Please contact us for delivery pricing.');
        }
      } else {
        setErrorMessage('Unable to calculate distance. Please verify your ZIP code and try again.');
      }
    } catch (error) {
      console.error('MapQuest API error:', error);
      setErrorMessage('Unable to calculate distance. Please contact us directly for delivery pricing.');
    } finally {
      setIsChecking(false);
    }
  };
  return <div className="w-full bg-gradient-to-br from-primary-50 via-white to-secondary-50" data-id="element-263">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20" data-id="element-264">
        {/* Hero Section */}
        <div className="flex flex-col lg:flex-row items-center gap-12 mb-20" data-id="element-265">
          <div className="lg:w-1/2 text-left" data-id="element-266">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-primary-500 leading-tight mb-6" data-id="element-267">
              The Ultimate Mobile Play Experience for Toddlers in Atlanta
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed" data-id="element-268">
              Premium soft play rentals delivered to your door. Safe, clean, and
              stress-free celebrations for your little ones.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-269">
              <Link to="/cart" className="inline-flex items-center justify-center px-8 py-4 bg-secondary-500 text-white rounded-full font-bold text-lg shadow-lg hover:bg-secondary-600 hover:shadow-xl transition-all transform hover:scale-105" onClick={() => window.scrollTo(0, 0)} data-id="element-270">
                Book Now
              </Link>
              <a href="#services" className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white rounded-full font-bold text-lg hover:bg-primary-700 transition-all shadow-lg" data-id="element-271">
                View Packages
              </a>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600" data-id="element-272">
              <div className="flex items-center gap-2" data-id="element-273">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-274" />
                <span data-id="element-275">Cleaned & Sanitized</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-276">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-277" />
                <span data-id="element-278">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-279">
                <CheckIcon className="w-5 h-5 text-green-500" data-id="element-280" />
                <span data-id="element-281">Setup Included</span>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2" data-id="element-282">
            <div className="relative" data-id="element-283">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a8JZZ9D2Nz2C5mrhGUsMe6/77BCF6CB-EA89-44A4-BDDF-18A46698D723-1024x768.jpg" alt="Colorful outdoor play setup with bounce house and soft play equipment" className="w-full h-auto rounded-3xl shadow-2xl" data-id="element-284" />
              <div className="absolute -bottom-6 -right-6 bg-secondary-500 text-white px-8 py-4 rounded-2xl font-heading font-bold text-xl shadow-xl" data-id="element-285">
                Safe & Fun! 🎉
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Distance Check Section */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 border-2 border-primary-100" data-id="element-286">
          <h3 className="text-2xl font-heading font-bold text-primary-600 mb-2" data-id="element-287">
            Check Your Delivery Cost
          </h3>
          <p className="text-gray-600 mb-6" data-id="element-288">
            Enter your Address or ZIP code to see pricing and availability
          </p>

          <form onSubmit={checkDeliveryDistance} className="space-y-4" data-id="element-289">
            <div data-id="element-290">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-291">
                Event Location
              </label>
              <input type="text" id="address" name="address" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter ZIP code (e.g., 30303)" className="py-4 px-5 block w-full text-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 border-2 border-gray-300 rounded-xl" data-id="element-292" />
            </div>

            <button type="submit" disabled={isChecking} className="w-full px-6 py-4 border-2 border-transparent text-lg font-bold rounded-xl shadow-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 flex items-center justify-center transition-all" data-id="element-293">
              {isChecking ? <>
                  <Loader2 className="animate-spin h-6 w-6 mr-2" data-id="element-294" />
                  Calculating...
                </> : 'Check Your Delivery Cost'}
            </button>
          </form>

          {errorMessage && <div className="mt-6 p-4 rounded-xl bg-red-50 border-2 border-red-200" data-id="element-295">
              <div className="flex items-start" data-id="element-296">
                <XIcon className="h-6 w-6 text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-297" />
                <div className="flex-1" data-id="element-298">
                  <span className="text-red-700 font-medium" data-id="element-299">
                    {errorMessage}
                  </span>
                  <p className="text-sm text-red-600 mt-2" data-id="element-300">
                    <Link to="/contact" className="underline font-bold hover:text-red-800" data-id="element-301">
                      Contact us directly
                    </Link>{' '}
                    for delivery pricing to your area.
                  </p>
                </div>
              </div>
            </div>}

          {checkResult !== null && <div className={`mt-6 p-6 rounded-xl border-2 ${checkResult.inRange ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`} data-id="element-302">
              {checkResult.inRange ? <div data-id="element-303">
                  <div className="flex items-center mb-3" data-id="element-304">
                    <CheckIcon className="h-6 w-6 text-green-600 mr-2" data-id="element-305" />
                    <span className="text-green-800 font-heading font-bold text-lg" data-id="element-306">
                      We deliver to your area!
                    </span>
                  </div>
                  <div className="text-gray-700 space-y-2 ml-8" data-id="element-307">
                    {checkResult.driveTime === 0 ? <p className="text-gray-600 italic" data-id="element-308">
                        This is our home location!
                      </p> : <p className="text-base" data-id="element-309">
                        <span className="font-semibold" data-id="element-310">Drive time:</span>{' '}
                        Approximately {checkResult.driveTime} minutes
                      </p>}
                    <p className="text-2xl font-heading font-bold text-green-800 mt-3" data-id="element-311">
                      Delivery cost:{' '}
                      {checkResult.shippingCost === 0 ? 'FREE 🎉' : `$${checkResult.shippingCost}`}
                    </p>
                  </div>
                </div> : <div data-id="element-312">
                  <div className="flex items-start" data-id="element-313">
                    <XIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" data-id="element-314" />
                    <div className="text-yellow-900" data-id="element-315">
                      <p className="font-heading font-bold text-lg mb-2" data-id="element-316">
                        Your location is over 60 minutes away
                      </p>
                      <p className="text-base" data-id="element-317">
                        Please{' '}
                        <Link to="/contact" className="underline font-bold hover:text-yellow-950" data-id="element-318">
                          contact us
                        </Link>{' '}
                        for a custom delivery quote and to check availability.
                      </p>
                    </div>
                  </div>
                </div>}
            </div>}
        </div>
      </div>
    </div>;
};